/**
 * Reusable exporters for CSV / XLSX / PDF used across modules.
 * - csv: array of row objects → CSV download
 * - xlsx: same, via SheetJS (xlsx)
 * - pdf: invoice-shaped object → A4 PDF via jsPDF + autoTable
 */
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { GATE_ACTIVITY_LIST, GATE_IN_FORM, CONTAINER_REGISTER } from "../assets/guide/guideImages";
import { getDepotBrand } from "./api";

// ─── MTG branding (single source of truth for every PDF) ──────────────────────
// Used by `drawMtgHeader` / `drawMtgFooter` below so the depot's letterhead is
// identical across cleaning certs, invoices, EIRs, FDAs, repair estimates and
// inspection reports. Updating the phone numbers, address or logo path here
// propagates to every exported document.
//
// Multi-depot (2026-10-30): this constant is now the GH-TEM fallback only.
// When an invoice carries a `depot_code` (e.g. TG-LFW), the exporter looks
// up the per-depot brand from AuthContext and passes it in as the `brand`
// argument to `drawMtgHeader` / `drawMtgFooter`. The MTG shape (name,
// address, phones, blue, lime, textMuted) is preserved so legacy callers
// without a brand argument still render identically.
export const MTG_BRAND = {
  name: "Multi Purpose Terminal Ghana Ltd. (MTG)",
  address: "Publishing Road, TEMA, GHANA",
  phones: ["+233 24 649 3715", "+233 24 520 4495"],
  blue: [0, 47, 167],
  lime: [153, 212, 32],
  textMuted: [110, 110, 110],
};

/**
 * Resolve the brand to use for an invoice/document. Falls back to the
 * global MTG defaults so pre-multi-depot callers keep working.
 */
export function resolveBrand(depotCode) {
  const b = getDepotBrand(depotCode);
  if (!b) return MTG_BRAND;
  return {
    name: b.legal_name || MTG_BRAND.name,
    address: (b.address_lines || []).join(", ") || MTG_BRAND.address,
    phones: (b.phones && b.phones.length) ? b.phones : MTG_BRAND.phones,
    blue: MTG_BRAND.blue,
    lime: MTG_BRAND.lime,
    textMuted: MTG_BRAND.textMuted,
    _extras: {
      trading_name: b.trading_name,
      email: b.email,
      tin: b.tin,
      vat_reg: b.vat_reg,
      country: b.country,
    },
  };
}

let _logoCacheGlobal = null;
async function _loadMtgLogo() {
  if (_logoCacheGlobal) return _logoCacheGlobal;
  try {
    const res = await fetch("/portside-logo.png", { cache: "force-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    const img = await new Promise((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.src = dataUrl;
    });
    _logoCacheGlobal = { dataUrl, width: img.naturalWidth, height: img.naturalHeight };
    return _logoCacheGlobal;
  } catch {
    return null;
  }
}

/**
 * Draw the MTG letterhead (white band + logo top-left + accent stripe +
 * optional right-aligned title block) at the top of any jsPDF page.
 * Returns the Y position immediately below the header so callers can place
 * their first content. Always call with `await`.
 */
export async function drawMtgHeader(doc, { title = "", subtitle = "", accent = "blue", headerH = 80, brand = null } = {}) {
  const b = brand || MTG_BRAND;
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 36;
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageW, headerH, "F");
  const stripe = accent === "lime" ? b.lime : b.blue;
  doc.setFillColor(stripe[0], stripe[1], stripe[2]);
  doc.rect(0, headerH, pageW, 4, "F");

  const logo = await _loadMtgLogo();
  if (logo) {
    const targetH = 56;
    const targetW = Math.round((logo.width / logo.height) * targetH);
    doc.addImage(logo.dataUrl, "PNG", margin, 12, targetW, targetH);
  } else {
    doc.setTextColor(b.blue[0], b.blue[1], b.blue[2]);
    doc.setFont("helvetica", "bold").setFontSize(20);
    doc.text("MTG", margin, 36);
    doc.setFont("helvetica", "normal").setFontSize(8);
    doc.text("OPERATIONS", margin, 50);
  }

  if (title) {
    doc.setTextColor(b.blue[0], b.blue[1], b.blue[2]);
    doc.setFont("helvetica", "bold").setFontSize(18);
    doc.text(title, pageW - margin, 36, { align: "right" });
    if (subtitle) {
      doc.setTextColor(b.textMuted[0], b.textMuted[1], b.textMuted[2]);
      doc.setFont("helvetica", "normal").setFontSize(10);
      doc.text(subtitle, pageW - margin, 54, { align: "right" });
    }
  }

  doc.setTextColor(0, 0, 0);
  return headerH + 12;
}

/**
 * Draw the MTG footer (company name, address, two phone numbers) on the
 * CURRENT page. Call once per page just before saving the PDF, or in a
 * page-loop for multi-page documents.
 */
export function drawMtgFooter(doc, { pageNumber, brand = null } = {}) {
  const b = brand || MTG_BRAND;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;
  const footerY = pageH - 28;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY - 12, pageW - margin, footerY - 12);
  doc.setTextColor(b.textMuted[0], b.textMuted[1], b.textMuted[2]);
  doc.setFont("helvetica", "normal").setFontSize(8);
  doc.text(b.name, margin, footerY);
  doc.text(b.address, margin, footerY + 9);
  if (b.phones && b.phones.length) {
    doc.text(`Tel: ${b.phones[0]}`, pageW - margin, footerY, { align: "right" });
    if (b.phones[1]) {
      doc.text(`        ${b.phones[1]}`, pageW - margin, footerY + 9, { align: "right" });
    }
  }
  if (pageNumber != null) {
    doc.text(`Page ${pageNumber}`, pageW / 2, footerY + 9, { align: "center" });
  }
  doc.setTextColor(0, 0, 0);
}

/** Apply `drawMtgFooter` to every existing page of the PDF. Call right before `doc.save()`. */
export function applyMtgFooterToAllPages(doc, { brand = null } = {}) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawMtgFooter(doc, { pageNumber: `${i} / ${total}`, brand });
  }
}



// EFTCO ECD code lookup (mirrors backend CLEANING_CODES). Update both sides together.
// P = procedures, F = food/feed additives, C = chemical additives,
// T = tests/inspections, E = equipment/special.
const PROC_CODES = {
  // P-codes
  "Hot water spin":                   "P01",
  "Hot water spin (T>80°C)":          "P09",
  "Hot water spin (T>60°C)":          "P10",
  "Steam clean":                      "P02",
  "Steaming":                         "P40",
  "Drying":                           "P30",
  "Kosher cleaning":                  "P26",
  "Halal cleaning":                   "P99",
  "Caustic wash":                     "P11",
  "Acid wash":                        "P12",
  "Solvent rinse":                    "P13",
  "Pre-rinse":                        "P15",
  "Final rinse":                      "P16",
  // F-codes
  "Cleaning with potable water only": "F01",
  "Food approved detergent":          "F50",
  // C-codes
  "Alkaline detergent":               "C01",
  "Detergent wash":                   "C10",
  // T-codes
  "Visual inspection":                "T01",
  "ATP measurement":                  "T42",
  "pH-value measurement":             "T43",
  "Allergen test":                    "T45",
  // E-codes
  "Cleaning of airlines":             "E60",
  "Cleaning of air connections":      "E61",
  "Sealing":                          "E90",
};
export const codeFor = (name) => PROC_CODES[name];
export const labelFor = (name) => {
  const c = PROC_CODES[name];
  return c ? `${c} — ${name}` : name;
};

const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export function downloadCsv(rows, filename, columns) {
  if (!rows?.length) return;
  const cols = columns || Object.keys(rows[0]);
  const lines = [cols.map((c) => c.label ?? c).join(",")];
  rows.forEach((r) => {
    lines.push(
      cols.map((c) => {
        const key = c.key ?? c;
        const fmt = c.format;
        const raw = r[key];
        return csvCell(fmt ? fmt(raw, r) : raw);
      }).join(",")
    );
  });
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadXlsx(rows, filename, columns, sheetName = "Sheet1") {
  if (!rows?.length) return;
  const cols = columns || Object.keys(rows[0]).map((k) => ({ key: k, label: k }));
  const data = rows.map((r) => {
    const out = {};
    cols.forEach((c) => {
      const key = c.key ?? c;
      const label = c.label ?? key;
      out[label] = c.format ? c.format(r[key], r) : r[key];
    });
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

const fmtMoney = (n, ccy = "USD") => {
  const v = Number(n || 0);
  return `${ccy} ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export async function downloadInvoicePdf(invoice, opts = {}) {
  // Water Solution invoices use a dedicated renderer — their line shape is
  // flat (date / tank / PO / fee) rather than the containers[] tree that
  // the standard wash/handling invoice carries.
  if (isWaterSolutionInvoice(invoice)) {
    return downloadWaterSolutionInvoicePdf(invoice, opts);
  }
  const brand = resolveBrand(invoice.depot_code);
  const doc = await buildInvoicePdf(invoice, opts);
  applyMtgFooterToAllPages(doc, { brand });
  doc.save(`${invoice.invoice_no}.pdf`);
}

export async function invoicePdfBlob(invoice, opts = {}) {
  if (isWaterSolutionInvoice(invoice)) {
    return waterSolutionInvoicePdfBlob(invoice, opts);
  }
  const brand = resolveBrand(invoice.depot_code);
  const doc = await buildInvoicePdf(invoice, opts);
  applyMtgFooterToAllPages(doc, { brand });
  return doc.output("blob");
}

async function buildInvoicePdf(invoice, { depotLocation = "Tema, Ghana (GHTEM)" } = {}) {
  const brand = resolveBrand(invoice.depot_code);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const ccy = invoice.currency || "USD";
  const margin = 40;

  // MTG letterhead (logo + accent stripe + right-aligned title)
  await drawMtgHeader(doc, { title: "INVOICE", subtitle: invoice.invoice_no, accent: "blue", brand });
  doc.setFontSize(10);
  doc.text(invoice.invoice_no, pageW - margin, 54, { align: "right" });

  doc.setTextColor(0, 0, 0);

  // Meta box
  const metaY = 100;
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("BILL TO", margin, metaY);
  doc.text("PERIOD", margin + 200, metaY);
  doc.text("ISSUED", pageW - margin - 100, metaY);

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(invoice.carrier_code, margin, metaY + 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(invoice.customer || `Carrier ${invoice.carrier_code}`, margin, metaY + 30);
  doc.text(depotLocation, margin, metaY + 44);

  const from = new Date(invoice.date_from).toISOString().slice(0, 10);
  const to = new Date(invoice.date_to).toISOString().slice(0, 10);
  doc.text(`${from}`, margin + 200, metaY + 16);
  doc.text(`to ${to}`, margin + 200, metaY + 30);
  doc.text(`Free days: ${invoice.free_days}`, margin + 200, metaY + 44);

  const issuedAt = invoice.created_at ? new Date(invoice.created_at) : new Date(invoice.generated_at || Date.now());
  doc.text(issuedAt.toISOString().slice(0, 10), pageW - margin - 100, metaY + 16);
  doc.text(issuedAt.toISOString().slice(11, 19), pageW - margin - 100, metaY + 30);

  // Customer Work Order — printed on repair invoices so the leasing co. AP
  // team can match the invoice to their authorisation record.
  if (invoice.customer_work_order && invoice.customer_work_order.wo_no) {
    const cwo = invoice.customer_work_order;
    const woY = metaY + 64;
    doc.setFontSize(8).setTextColor(120, 120, 120);
    doc.text("CUSTOMER WORK ORDER", margin, woY);
    doc.setTextColor(0, 0, 0).setFont("helvetica", "bold").setFontSize(10);
    doc.text(cwo.wo_no, margin, woY + 14);
    doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(80, 80, 80);
    const parts = [];
    if (cwo.wo_date) parts.push(`dated ${new Date(cwo.wo_date).toLocaleDateString("en-GB")}`);
    if (cwo.authorised_by) parts.push(`authorised by ${cwo.authorised_by}`);
    if (parts.length) doc.text(parts.join(" · "), margin, woY + 26);
    doc.setTextColor(0, 0, 0);
  }

  // STC PO references — printed when any wash line on this invoice carries a
  // Stolt cleaning-instruction PO. Accounts no longer has to dig through
  // email to match the invoice to the PO.
  const stcPos = Array.isArray(invoice.stc_po_numbers) ? invoice.stc_po_numbers.filter(Boolean) : [];
  let tableStartY = metaY + 70;
  if (stcPos.length) {
    const poY = invoice.customer_work_order?.wo_no ? metaY + 100 : metaY + 64;
    doc.setFontSize(8).setTextColor(120, 120, 120);
    doc.text("STC PO REFERENCES", margin, poY);
    doc.setTextColor(0, 47, 167).setFont("helvetica", "bold").setFontSize(10);
    // Wrap if the PO list is long
    const poText = stcPos.join("  ·  ");
    const wrapped = doc.splitTextToSize(poText, pageW - margin * 2 - 220);
    wrapped.forEach((ln, i) => doc.text(ln, margin, poY + 14 + i * 12));
    doc.setTextColor(0, 0, 0).setFont("helvetica", "normal");
    tableStartY = poY + 14 + wrapped.length * 12 + 10;
  }

  // Line items table — flattened, grouped by tank_category. We add separator
  // rows so the carrier (Stolt) can immediately see how much of each cleaning
  // class they're being billed for.
  const body = [];
  const groups = new Map();   // category → containers[]
  (invoice.containers || []).forEach((c) => {
    const key = c.tank_category || "UNCATEGORIZED";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  });
  // Order: FOOD → CHEM → UNCATEGORIZED
  const order = ["FOOD", "CHEM", "UNCATEGORIZED"];
  const sortedKeys = [...groups.keys()].sort((a, b) =>
    (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) -
    (order.indexOf(b) === -1 ? 99 : order.indexOf(b))
  );
  for (const key of sortedKeys) {
    const containersInGroup = groups.get(key);
    const groupSubtotal = containersInGroup.reduce((s, c) => s + (c.subtotal || 0), 0);
    const label = key === "FOOD" ? "FOOD-GRADE CLEANINGS"
      : key === "CHEM" ? "CHEMICAL CLEANINGS" : "UNCATEGORIZED";
    body.push([
      { content: `-- ${label}  ·  ${containersInGroup.length} containers  ·  ${fmtMoney(groupSubtotal, ccy)}`,
        colSpan: 7, styles: { fontStyle: "bold", fillColor: [240, 240, 240], halign: "left" } },
    ]);
    containersInGroup.forEach((c) => {
      (c.lines || []).forEach((l) => {
        body.push([
          c.container_number,
          `${c.size_ft}ft`,
          l.service,
          l.description,
          String(l.qty),
          fmtMoney(l.rate, ccy),
          fmtMoney(l.amount, ccy),
        ]);
      });
    });
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [["Container", "Size", "Service", "Description", "Qty", "Rate", "Amount"]],
    body,
    headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 4 },
    columnStyles: {
      0: { cellWidth: 75, font: "courier" },
      1: { cellWidth: 35, halign: "center" },
      2: { cellWidth: 70 },
      3: { cellWidth: "auto" },
      4: { cellWidth: 30, halign: "right" },
      5: { cellWidth: 60, halign: "right", font: "courier" },
      6: { cellWidth: 70, halign: "right", font: "courier", fontStyle: "bold" },
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      // Page number
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(str, pageW - margin, doc.internal.pageSize.getHeight() - 20, { align: "right" });
    },
  });

  // Totals box
  const endY = doc.lastAutoTable.finalY + 20;
  const boxX = pageW - margin - 220;
  doc.setDrawColor(220, 220, 220);
  doc.rect(boxX, endY, 220, 80);

  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text("Containers", boxX + 10, endY + 18);
  doc.text("Line items", boxX + 10, endY + 34);
  doc.text("Subtotal", boxX + 10, endY + 52);

  doc.setTextColor(0, 0, 0);
  doc.setFont("courier", "normal");
  doc.text(String(invoice.totals.containers), boxX + 210, endY + 18, { align: "right" });
  doc.text(String(invoice.totals.line_items), boxX + 210, endY + 34, { align: "right" });
  doc.text(fmtMoney(invoice.totals.subtotal, ccy), boxX + 210, endY + 52, { align: "right" });

  doc.setFillColor(0, 47, 167);
  doc.rect(boxX, endY + 60, 220, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("GRAND TOTAL", boxX + 10, endY + 75);
  doc.setFont("courier", "bold");
  doc.text(fmtMoney(invoice.totals.grand_total, ccy), boxX + 210, endY + 75, { align: "right" });

  // Footer
  doc.setTextColor(150, 150, 150);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(
    "This is a system-generated invoice from MTG (a Portside group company). Storage charged on stays beyond free days.",
    margin,
    doc.internal.pageSize.getHeight() - 20
  );

  return doc;
}

// ─────────── Water Solution invoice PDF ───────────
// Separate renderer because WS invoices have a different shape than
// handling/wash invoices: lines are flat (tank + PO + date + fee), there are
// no containers / wash categories / free days. We re-use the same MTG
// letterhead and totals-box styling so finance sees a visually identical
// document — just with WS-specific line fields.

/** Return true when this invoice should be rendered by the Water Solution
 *  PDF builder (shape differs from the standard wash/handling invoice). */
export function isWaterSolutionInvoice(invoice) {
  return (invoice?.invoice_type || "").toUpperCase() === "WATER_SOLUTION";
}

export async function downloadWaterSolutionInvoicePdf(invoice, opts = {}) {
  const brand = resolveBrand(invoice.depot_code);
  const doc = await buildWaterSolutionInvoicePdf(invoice, opts);
  applyMtgFooterToAllPages(doc, { brand });
  doc.save(`${invoice.invoice_no}.pdf`);
}

export async function waterSolutionInvoicePdfBlob(invoice, opts = {}) {
  const brand = resolveBrand(invoice.depot_code);
  const doc = await buildWaterSolutionInvoicePdf(invoice, opts);
  applyMtgFooterToAllPages(doc, { brand });
  return doc.output("blob");
}

async function buildWaterSolutionInvoicePdf(invoice, { depotLocation = "Tema, Ghana (GHTEM)" } = {}) {
  const brand = resolveBrand(invoice.depot_code);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const ccy = invoice.currency || "USD";
  const margin = 40;

  await drawMtgHeader(doc, { title: "INVOICE", subtitle: invoice.invoice_no, accent: "blue", brand });
  doc.setFontSize(10);
  doc.text(invoice.invoice_no, pageW - margin, 54, { align: "right" });
  doc.setTextColor(0, 0, 0);

  // Meta row — Bill-to / Period / Issued (matches the wash invoice meta row).
  const metaY = 100;
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("BILL TO", margin, metaY);
  doc.text("PERIOD", margin + 200, metaY);
  doc.text("ISSUED", pageW - margin - 100, metaY);

  const carrier = invoice.carrier_code || invoice.customer_code || "—";
  doc.setTextColor(0, 0, 0).setFont("helvetica", "bold").setFontSize(11);
  doc.text(carrier, margin, metaY + 16);
  doc.setFont("helvetica", "normal").setFontSize(9);
  doc.text(invoice.customer_legal_name || invoice.customer || `Carrier ${carrier}`,
           margin, metaY + 30);
  doc.text(depotLocation, margin, metaY + 44);

  // Period = YYYY-MM for WS invoices; render it as the month, falling back
  // to a date_from → date_to range if the backend fills those in.
  const period = invoice.period || "";
  doc.text(period || `${(invoice.date_from || "").slice(0, 10)}`, margin + 200, metaY + 16);
  if (!period) {
    doc.text(`to ${(invoice.date_to || "").slice(0, 10)}`, margin + 200, metaY + 30);
  }
  doc.setTextColor(120, 120, 120).setFontSize(8);
  doc.text("Service: Water Solution (flat-fee rinse)", margin + 200, metaY + 44);
  doc.setTextColor(0, 0, 0).setFontSize(9);

  const issuedAt = invoice.created_at ? new Date(invoice.created_at) : new Date();
  doc.text(issuedAt.toISOString().slice(0, 10), pageW - margin - 100, metaY + 16);
  doc.text(issuedAt.toISOString().slice(11, 19), pageW - margin - 100, metaY + 30);

  // Line-items table — one row per WS record.
  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const body = lines.map((l) => [
    (l.service_date || "").slice(0, 10),
    l.tank_number || "—",
    l.po_number || "—",
    l.description || "Water Solution",
    String(l.qty ?? 1),
    fmtMoney(l.rate, ccy),
    fmtMoney(l.amount, ccy),
  ]);

  autoTable(doc, {
    startY: metaY + 70,
    head: [["Service date", "Tank", "PO", "Description", "Qty", "Rate", "Amount"]],
    body,
    headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 4 },
    columnStyles: {
      0: { cellWidth: 75, font: "courier" },
      1: { cellWidth: 85, font: "courier" },
      2: { cellWidth: 85, font: "courier" },
      3: { cellWidth: "auto" },
      4: { cellWidth: 30, halign: "right" },
      5: { cellWidth: 60, halign: "right", font: "courier" },
      6: { cellWidth: 70, halign: "right", font: "courier", fontStyle: "bold" },
    },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8).setTextColor(150, 150, 150);
      doc.text(str, pageW - margin, doc.internal.pageSize.getHeight() - 20, { align: "right" });
    },
  });

  const subtotal = Number(invoice.subtotal ?? invoice.totals?.subtotal ?? 0);
  const grandTotal = Number(invoice.total ?? invoice.totals?.grand_total ?? subtotal);
  const lineCount = lines.length;

  // Totals box — identical visual to the wash invoice totals block.
  const endY = doc.lastAutoTable.finalY + 20;
  const boxX = pageW - margin - 220;
  doc.setDrawColor(220, 220, 220);
  doc.rect(boxX, endY, 220, 80);
  doc.setFontSize(9).setTextColor(80, 80, 80);
  doc.text("Jobs", boxX + 10, endY + 18);
  doc.text("Line items", boxX + 10, endY + 34);
  doc.text("Subtotal", boxX + 10, endY + 52);
  doc.setTextColor(0, 0, 0).setFont("courier", "normal");
  doc.text(String(lineCount), boxX + 210, endY + 18, { align: "right" });
  doc.text(String(lineCount), boxX + 210, endY + 34, { align: "right" });
  doc.text(fmtMoney(subtotal, ccy), boxX + 210, endY + 52, { align: "right" });
  doc.setFillColor(0, 47, 167);
  doc.rect(boxX, endY + 60, 220, 22, "F");
  doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(10);
  doc.text("GRAND TOTAL", boxX + 10, endY + 75);
  doc.setFont("courier", "bold");
  doc.text(fmtMoney(grandTotal, ccy), boxX + 210, endY + 75, { align: "right" });

  doc.setTextColor(150, 150, 150).setFont("helvetica", "normal").setFontSize(7);
  doc.text(
    "This is a system-generated Water Solution invoice from MTG (a Portside group company). Flat-fee STC rinse billing.",
    margin,
    doc.internal.pageSize.getHeight() - 20
  );

  return doc;
}

// ─────────── FDA (rental) PDF ───────────
export async function downloadFdaPdf(fda) {
  const brand = resolveBrand(fda.depot_code);
  const doc = await buildFdaPdf(fda);
  applyMtgFooterToAllPages(doc, { brand });
  doc.save(`${fda.fda_no || "FDA"}.pdf`);
}

export async function fdaPdfBlob(fda) {
  const brand = resolveBrand(fda.depot_code);
  const doc = await buildFdaPdf(fda);
  applyMtgFooterToAllPages(doc, { brand });
  return doc.output("blob");
}

async function buildFdaPdf(fda) {
  const brand = resolveBrand(fda.depot_code);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 36;
  const W = doc.internal.pageSize.getWidth();

  await drawMtgHeader(doc, { title: "RENTAL STATEMENT", subtitle: fda.fda_no || "DRAFT", accent: "blue", brand });

  const ccy = fda.currency || "USD";
  const fx = fda.eur_to_usd || 1;
  const isEur = ccy === "EUR";
  const grandTotalNative = isEur
    ? Number(fda.totals?.grand_total_eur ?? 0)
    : Number(fda.totals?.grand_total ?? 0);
  // Convert a USD amount back to the line currency for display.
  const lineCcy = (usd) => isEur ? ((usd || 0) / (fx || 1)) : (usd || 0);

  doc.setFontSize(10);
  const meta = [
    ["FDA No.",       fda.fda_no || "—"],
    ["Customer",      fda.customer_name || fda.customer_code],
    ["Period",        `${fda.period_from} to ${fda.period_to}`],
    ["Currency",      ccy],
    ["Tanks billed",  String(fda.totals?.line_count ?? 0)],
    ["Grand total",   `${ccy} ${grandTotalNative.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
  ];
  let y = 100;
  meta.forEach(([k, v]) => {
    doc.setFont("helvetica", "bold");  doc.text(k + ":", margin, y);
    doc.setFont("helvetica", "normal"); doc.text(String(v), margin + 90, y);
    y += 14;
  });

  if (fda.fx_warning) {
    doc.setFont("helvetica", "bold").setTextColor(180, 80, 0);
    doc.text("FX warning: some EUR amounts could not be converted - see Rentals > FX Rates.", margin, y + 8);
    doc.setTextColor(0, 0, 0);
    y += 16;
  }

  autoTable(doc, {
    startY: y + 8,
    head: [["Tank", "Pickup", "Drop-off", "Days", "Rate", "Rental", "Handling", "Mobilis.", "Cleaning", "Trucking", `Total ${ccy}`]],
    body: (fda.lines || []).map((ln) => {
      const rentalNative = isEur
        ? ((ln.rental_native || 0) + (ln.lumpsum_native_eur || 0))
        : (ln.rental_usd || 0);
      const lineTotalNative = isEur
        ? (rentalNative + lineCcy(ln.handling_usd) + lineCcy(ln.mobilisation_usd) + lineCcy(ln.cleaning_usd) + lineCcy(ln.trucking_usd))
        : (ln.line_total_usd || 0);
      const hasStepDown = (ln.days_at_step_down_rate || 0) > 0 && (ln.step_down_daily_rate || 0) > 0;
      const rateCell = hasStepDown
        ? `${ln.rate_currency || ccy} ${ln.daily_rate} / ${ln.step_down_daily_rate}`
        : `${ln.rate_currency || ccy} ${ln.daily_rate}`;
      const daysCell = hasStepDown
        ? `${ln.rental_days_in_month} (${ln.days_at_initial_rate}+${ln.days_at_step_down_rate})`
        : ln.rental_days_in_month;
      return [
        ln.tank_number,
        ln.pickup_date || "—",
        ln.orphan_services_only ? "—" : (ln.drop_off_date || "ON-HIRE"),
        daysCell,
        rateCell,
        rentalNative.toFixed(2),
        lineCcy(ln.handling_usd).toFixed(2),
        lineCcy(ln.mobilisation_usd).toFixed(2),
        lineCcy(ln.cleaning_usd).toFixed(2),
        lineCcy(ln.trucking_usd).toFixed(2),
        lineTotalNative.toFixed(2),
      ];
    }),
    theme: "grid",
    styles: { fontSize: 7.5, cellPadding: 3 },
    headStyles: { fillColor: [0, 49, 71], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { fontStyle: "bold" },
      3: { halign: "right" },
      5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" },
      8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right", fontStyle: "bold" },
    },
    foot: [["", "", "", "", "", "", "", "", "", "TOTAL", `${ccy} ${grandTotalNative.toFixed(2)}`]],
    footStyles: { fillColor: [240, 240, 240], fontStyle: "bold" },
  });

  // ── Off-hire costs section (optional) ───────────────────────────────
  const offhireLines = fda.offhire_lines || [];
  if (offhireLines.length) {
    const offhireTotalNative = isEur
      ? Number(fda.totals?.offhire_total_eur ?? 0)
      : Number(fda.totals?.offhire_total_usd ?? 0);
    const y2 = doc.lastAutoTable.finalY + 20;
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("Off-hire costs", margin, y2);
    autoTable(doc, {
      startY: y2 + 6,
      head: [["Date", "Tank", "Category", "Description", "Vendor", `Amount ${ccy}`]],
      body: offhireLines.map((ln) => [
        ln.incurred_at || "",
        ln.tank_number || "",
        ln.category || "",
        ln.description || "",
        ln.vendor || "",
        (isEur && ln.invoice_currency !== "EUR"
          ? ((ln.invoice_amount_native || 0) / (fx || 1))
          : (ln.invoice_amount_native || 0)
        ).toFixed(2),
      ]),
      theme: "grid",
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [180, 100, 20], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        1: { fontStyle: "bold" },
        5: { halign: "right", fontStyle: "bold" },
      },
      foot: [["", "", "", "", "SUBTOTAL", `${ccy} ${offhireTotalNative.toFixed(2)}`]],
      footStyles: { fillColor: [240, 230, 215], fontStyle: "bold" },
    });
  }

  // ── Service event details (optional) ────────────────────────────────
  // Shows the per-event descriptions behind the Handling/Mobilisation/
  // Cleaning/Trucking columns in the main table — otherwise the customer
  // only sees a bare amount with no explanation of what they're paying for.
  const billedEvents = (fda.billed_events || []).filter(
    (e) => e.event_type !== "LUMPSUM_RENTAL" && (e.amount_usd || 0) > 0,
  );
  if (billedEvents.length) {
    const y3 = doc.lastAutoTable.finalY + 20;
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("Service event details", margin, y3);
    autoTable(doc, {
      startY: y3 + 6,
      head: [["Date", "Tank", "Type", "Description", `Amount ${ccy}`]],
      body: billedEvents.map((e) => [
        e.event_date || "",
        e.tank_number || "",
        (e.event_type || "").replace("_", " "),
        e.notes || "—",
        (isEur && e.amount_currency !== "EUR"
          ? ((e.amount_native || 0) / (fx || 1))
          : (e.amount_native || 0)
        ).toFixed(2),
      ]),
      theme: "grid",
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [50, 90, 130], textColor: 255, fontStyle: "bold" },
      columnStyles: {
        1: { fontStyle: "bold" },
        4: { halign: "right" },
      },
    });
  }

  doc.setFontSize(8).setTextColor(120);
  doc.text(
    "FDA generated by Portside Operations · monthly rental statement.",
    margin, doc.internal.pageSize.getHeight() - 20,
  );
  return doc;
}

export function invoiceToCsv(invoice) {
  const ccy = invoice.currency || "USD";
  const headers = ["Container", "ISO", "Size", "Service", "Description", "Event Time", "Qty", "Rate", "Amount", "Currency"];
  const rows = [headers.join(",")];
  (invoice.containers || []).forEach((c) => {
    (c.lines || []).forEach((l) => {
      rows.push([
        c.container_number, c.iso_type, `${c.size_ft}ft`,
        l.service, csvCell(l.description),
        new Date(l.event_time).toISOString(),
        l.qty, Number(l.rate).toFixed(2), Number(l.amount).toFixed(2), ccy,
      ].join(","));
    });
  });
  rows.push("");
  rows.push(`,,,,,,,Subtotal,${invoice.totals.subtotal.toFixed(2)},${ccy}`);
  rows.push(`,,,,,,,Grand Total,${invoice.totals.grand_total.toFixed(2)},${ccy}`);
  const blob = new Blob(["\uFEFF" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${invoice.invoice_no}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}


const DAMAGE_CODE_LABELS = {
  BR: "Broken", BT: "Bent", CR: "Crack", CT: "Cut", DH: "Hole", DT: "Dent",
  RU: "Rust", MS: "Missing", HO: "Holed", BN: "Burn", LO: "Loose", SC: "Scratched",
};

/**
 * Draw a labeled container panel (rectangle outline + corner posts + corrugations + label).
 * x,y = top-left in document units (points). w,h = dimensions.
 */
function drawPanel(doc, x, y, w, h, label, isEnd = false) {
  doc.setDrawColor(11, 18, 32);
  doc.setLineWidth(1);
  doc.setFillColor(255, 255, 255);
  doc.rect(x, y, w, h, "FD");
  // Corner posts
  doc.setFillColor(11, 18, 32);
  doc.rect(x, y, 3, h, "F");
  doc.rect(x + w - 3, y, 3, h, "F");
  if (isEnd) {
    doc.rect(x, y, w, 3, "F");
    doc.rect(x, y + h - 3, w, 3, "F");
  }
  // Corrugations (light vertical lines on side views)
  if (!isEnd) {
    doc.setDrawColor(200, 213, 225);
    doc.setLineWidth(0.2);
    const cols = 16;
    for (let i = 0; i < cols; i++) {
      const cx = x + (w / cols) * (i + 0.5);
      doc.line(cx, y + 3, cx, y + h - 3);
    }
  }
  // Label
  doc.setFontSize(7);
  doc.setTextColor(11, 18, 32);
  doc.setFont("helvetica", "bold");
  doc.text(label, x + 3, y - 2);
}

function drawDamageMarker(doc, cx, cy, n, severity) {
  const color = severity === "CRITICAL" ? [220, 38, 38] :
                severity === "MAJOR"    ? [234, 88, 12]  :
                                          [255, 186, 8];
  doc.setFillColor(...color);
  doc.setDrawColor(11, 18, 32);
  doc.setLineWidth(0.4);
  doc.circle(cx, cy, 3.5, "FD");
  doc.setTextColor(11, 18, 32);
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text(String(n), cx, cy + 1.6, { align: "center" });
}

/**
 * Render a printable EIR (Equipment Interchange Receipt) PDF.
 */
export async function downloadEirPdf(eir) {
  // Landscape A4 by request (2026-09-02) — depot ops prefers the wider
  // sheet so gate-in / gate-out sit side-by-side without cramping and the
  // three signature blocks along the bottom get more elbow room for the
  // driver/surveyor to sign cleanly. Layout scales off pageW/pageH so the
  // rest of the render code is untouched.
  const brand = resolveBrand(eir.depot_code);
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;

  // MTG letterhead
  await drawMtgHeader(doc, { title: "EQUIPMENT INTERCHANGE RECEIPT", subtitle: eir.eir_no, accent: "blue", brand });

  // ---- Container & carrier info block ----
  const infoY = 100;
  doc.setDrawColor(220, 220, 220);
  doc.rect(margin, infoY, pageW - margin * 2, 60);

  const cell = (label, value, cx, cy, w = 130) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(label, cx, cy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(String(value || "—"), cx, cy + 12, { maxWidth: w });
  };

  cell("CONTAINER NUMBER", eir.container_number, margin + 8, infoY + 12);
  cell("ISO TYPE", eir.container_iso_type, margin + 150, infoY + 12);
  cell("CARRIER", eir.carrier_code, margin + 230, infoY + 12);
  cell("DEPOT", `${eir.depot_name || ""} (${eir.depot_code || ""})`, margin + 310, infoY + 12, 220);

  cell("CUSTOMER / PRINCIPAL", eir.customer, margin + 8, infoY + 38, 200);
  cell("STATUS", eir.status, margin + 230, infoY + 38);
  cell("CREATED BY", eir.created_by, margin + 310, infoY + 38, 220);

  // ---- Damage diagram ----
  const diagY = 144;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("CONTAINER DAMAGE DIAGRAM", margin, diagY - 4);

  const longW = 260, longH = 32;
  const endW = 70, endH = 70;
  const panels = [
    { side: "TOP",    x: margin,      y: diagY + 6,   w: longW, h: longH, isEnd: false, label: "TOP" },
    { side: "LEFT",   x: margin,      y: diagY + 48,  w: longW, h: longH, isEnd: false, label: "LEFT" },
    { side: "BOTTOM", x: margin,      y: diagY + 90,  w: longW, h: longH, isEnd: false, label: "BOTTOM" },
    { side: "RIGHT",  x: margin,      y: diagY + 132, w: longW, h: longH, isEnd: false, label: "RIGHT" },
    { side: "FRONT",  x: margin + longW + 20, y: diagY + 6,   w: endW,  h: endH, isEnd: true,  label: "FRONT (door end)" },
    { side: "BACK",   x: margin + longW + 20, y: diagY + 90,  w: endW,  h: endH, isEnd: true,  label: "BACK" },
  ];
  panels.forEach((p) => drawPanel(doc, p.x, p.y, p.w, p.h, p.label, p.isEnd));

  // Plot damage markers
  (eir.gate_in?.damages || []).forEach((d, i) => {
    const p = panels.find((q) => q.side === d.side);
    if (!p) return;
    const cx = p.x + (d.x / 100) * p.w;
    const cy = p.y + (d.y / 100) * p.h;
    drawDamageMarker(doc, cx, cy, i + 1, d.severity);
  });

  // Legend
  const legendY = diagY + 170;
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(120, 120, 120);
  doc.text("LEGEND  ·  marker color = severity", margin, legendY);
  [
    { c: [255, 186, 8],  l: "Minor" },
    { c: [234, 88, 12],  l: "Major" },
    { c: [220, 38, 38],  l: "Critical" },
  ].forEach((m, i) => {
    const lx = margin + 95 + i * 55;
    doc.setFillColor(...m.c);
    doc.circle(lx, legendY - 2, 3, "F");
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.text(m.l, lx + 6, legendY);
  });

  // ---- Damage list table ----
  const damages = eir.gate_in?.damages || [];
  if (damages.length > 0) {
    autoTable(doc, {
      startY: legendY + 8,
      head: [["#", "Code", "Side", "Severity", "Description"]],
      body: damages.map((d, i) => [
        String(i + 1),
        `${d.code} — ${DAMAGE_CODE_LABELS[d.code] || ""}`,
        d.side,
        d.severity || "MINOR",
        d.description || "",
      ]),
      headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 20, halign: "center" },
        1: { cellWidth: 80 },
        2: { cellWidth: 50 },
        3: { cellWidth: 50 },
        4: { cellWidth: "auto" },
      },
      margin: { left: margin, right: margin },
    });
  }

  let cursorY = damages.length > 0 ? doc.lastAutoTable.finalY + 14 : legendY + 18;
  if (cursorY > pageH - 240) {
    doc.addPage();
    cursorY = margin;
  }

  // ---- Gate IN / Gate OUT side-by-side ----
  const colW = (pageW - margin * 2 - 12) / 2;
  const drawSection = (title, gx, gy, data) => {
    doc.setFillColor(245, 247, 250);
    doc.rect(gx, gy, colW, 18, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 47, 167);
    doc.text(title, gx + 6, gy + 12);

    doc.setDrawColor(220, 220, 220);
    doc.rect(gx, gy + 18, colW, 130);

    const rows = data;
    rows.forEach((r, idx) => {
      const ry = gy + 32 + idx * 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text(r.label, gx + 6, ry);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text(String(r.value || "—"), gx + 90, ry, { maxWidth: colW - 96 });
    });
  };

  const gi = eir.gate_in || {};
  const go = eir.gate_out || {};
  const fmtTime = (t) => t ? new Date(t).toLocaleString() : "—";

  // Landscape A4 has ~595pt of vertical space vs ~842pt in portrait, so a
  // 0-damage EIR would push the remarks over the signature strip at the
  // very bottom. Force a page break here if the gate sections + remarks +
  // signatures wouldn't all fit below cursorY on the current page.
  const GATE_SECTION_H = 148;
  const REMARKS_BLOCK_H = 40;
  const SIG_STRIP_H = 100;   // sig line + labels + a safety pad
  const needed = GATE_SECTION_H + REMARKS_BLOCK_H + SIG_STRIP_H;
  if (cursorY + needed > pageH - margin) {
    doc.addPage();
    cursorY = margin;
  }

  drawSection("GATE IN", margin, cursorY, [
    { label: "Date / time", value: fmtTime(gi.event_time) },
    { label: "Driver",       value: gi.driver_name },
    { label: "Truck plate",  value: gi.truck_plate },
    { label: "Seal #",       value: gi.seal_number },
    { label: "Grade",        value: gi.grade },
    { label: "Condition",    value: gi.condition },
    { label: "Surveyor",     value: gi.surveyor_name },
  ]);

  drawSection("GATE OUT", margin + colW + 12, cursorY, [
    { label: "Date / time", value: fmtTime(go.event_time) },
    { label: "Driver",       value: go.driver_name },
    { label: "Truck plate",  value: go.truck_plate },
    { label: "Seal #",       value: go.seal_number },
    { label: "Condition",    value: go.condition },
    { label: "Released to",  value: go.released_to },
    { label: "Closed by",    value: eir.closed_by },
  ]);

  // Remarks
  cursorY += 160;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("REMARKS (gate-in)", margin, cursorY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text(gi.remarks || "—", margin, cursorY + 12, { maxWidth: pageW - margin * 2 });

  // ---- Signature blocks ----
  const sigY = pageH - 90;
  const sigW = (pageW - margin * 2 - 24) / 3;
  ["DEPOT OPERATOR", "DRIVER", "SURVEYOR / SUPERVISOR"].forEach((label, i) => {
    const sx = margin + i * (sigW + 12);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(sx, sigY, sx + sigW, sigY);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(label, sx, sigY + 12);
    doc.setFontSize(7);
    doc.text("Name & signature", sx, sigY + 22);
    doc.text("Date: _______________", sx + sigW - 80, sigY + 22);
  });

  // Footer drawn by applyMtgFooterToAllPages

  applyMtgFooterToAllPages(doc, { brand });
  doc.save(`${eir.eir_no}.pdf`);
}

/**
/**
 * Internal builder — draws the waybill onto a jsPDF doc and returns it.
 * Extracted so we can (a) download the PDF and (b) POST the same bytes to
 * the backend for auto-email without duplicating layout code.
 *
 * Layout: **two identical A5-landscape copies stacked on one A4 portrait
 * sheet** — top copy for the depot, bottom copy for the driver. Cuts paper
 * and toner in half vs. the old single-A4 layout. A dashed guide across
 * the middle tells the operator where to cut.
 */
async function _buildGateWaybillDoc(event, photos = []) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();     // 595
  const pageH = doc.internal.pageSize.getHeight();    // 842
  const halfH = pageH / 2;                            // 421

  const gateType = (event.gate_type || "IN").toUpperCase();
  const fullEmpty = event.is_full ? "FULL" : "EMPTY";

  const fmtTime = (t) => (t ? new Date(t).toLocaleString("en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }) : "—");

  const leftRows = [
    { label: "Depot", value: event.location_name || event.yard_code },
    { label: "Fleet manager", value: event.carrier_code },
    { label: "Date / time", value: fmtTime(event.event_time) },
    { label: "Container number", value: event.container_number },
    { label: "ISO code", value: event.container_iso_type },
    { label: "Vessel", value: event.vessel_name },
    { label: "Grade", value: event.grade },
    { label: "Condition", value: event.condition },
    { label: "Booking / Order", value: event.booking_number },
  ];
  const rightRows = [
    { label: "Customer", value: event.customer },
    { label: "Carrier", value: event.carrier_code },
    { label: "Transport id", value: event.transport_ref },
    { label: "Seal number", value: event.seal_number },
    { label: "Contact number", value: event.contact_number },
    { label: "Driver name", value: event.driver_name },
    { label: "Driver license", value: event.driver_license },
    { label: "Truck plate", value: event.truck_plate },
    { label: "Comment", value: event.notes || event.condition },
  ];

  // Render one A5-landscape "copy" starting at (0, yOffset). Compact spacing
  // vs the legacy full-A4 layout so both copies fit cleanly.
  const drawCopy = (yOffset, copyLabel) => {
    const margin = 24;
    const innerW = pageW - margin * 2;
    const colW = (innerW - 10) / 2;
    const rowH = 15;                                  // was 22 on full A4
    const headBarH = 14;                              // was 18

    // Top banner — copy label + container line (compact replacement for the
    // MTG "drawMtgHeader" block which is far too tall for the A5 half).
    doc.setFillColor(MTG_BRAND.blue[0], MTG_BRAND.blue[1], MTG_BRAND.blue[2]);
    doc.rect(0, yOffset, pageW, 26, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text("EQUIPMENT INTERCHANGE RECEIPT", margin, yOffset + 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(`GATE-${gateType} ${fullEmpty} · ${event.container_number || ""}`, margin, yOffset + 22);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(copyLabel, pageW - margin, yOffset + 17, { align: "right" });

    // Two data columns
    const bodyY = yOffset + 34;
    const drawColumn = (gx, gy, headTitle, rows) => {
      doc.setFillColor(245, 247, 250);
      doc.rect(gx, gy, colW, headBarH, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(MTG_BRAND.blue[0], MTG_BRAND.blue[1], MTG_BRAND.blue[2]);
      doc.text(headTitle, gx + 6, gy + 10);

      const contentH = rows.length * rowH + 6;
      doc.setDrawColor(220, 220, 220);
      doc.rect(gx, gy + headBarH, colW, contentH);

      rows.forEach((r, idx) => {
        const ry = gy + headBarH + 12 + idx * rowH;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(MTG_BRAND.textMuted[0], MTG_BRAND.textMuted[1], MTG_BRAND.textMuted[2]);
        doc.text(r.label.toUpperCase(), gx + 6, ry);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        doc.text(String(r.value == null || r.value === "" ? "—" : r.value),
                 gx + 90, ry, { maxWidth: colW - 96 });
      });
      return gy + headBarH + contentH;
    };
    drawColumn(margin, bodyY, "MOVEMENT & CONTAINER", leftRows);
    drawColumn(margin + colW + 10, bodyY, "PARTIES & TRANSPORT", rightRows);

    // Signature strip near the bottom of THIS copy — 3 boxes, tight.
    // Reserve 42pt of clear space at the very bottom of each half so we
    // don't collide with the page footer (top copy) or run off-page
    // (bottom copy).
    const copyBottom = yOffset + halfH - 42;
    const sigY = copyBottom - 4;
    const sigW = (innerW - 20) / 3;
    ["DEPOT OPERATOR", "DRIVER", "CARRIER REP"].forEach((label, i) => {
      const sx = margin + i * (sigW + 10);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(sx, sigY, sx + sigW, sigY);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(label, sx, sigY + 10);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      doc.setTextColor(MTG_BRAND.textMuted[0], MTG_BRAND.textMuted[1], MTG_BRAND.textMuted[2]);
      doc.text("Name & signature", sx, sigY + 18);
      doc.text("Date: _____________", sx + sigW - 72, sigY + 18);
    });
  };

  drawCopy(0, "COPY 1 — DEPOT");
  drawCopy(halfH, "COPY 2 — DRIVER");

  // Dashed cut line + scissors icon across the middle. Text sits ABOVE
  // the line and to the right so it doesn't collide with the second
  // copy's blue banner (which starts at halfH).
  doc.setDrawColor(150, 150, 150);
  doc.setLineDashPattern([3, 3], 0);
  doc.line(20, halfH, pageW - 20, halfH);
  doc.setLineDashPattern([], 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(140, 140, 140);
  doc.text("- - -  cut here  - - -", pageW / 2, halfH - 3, { align: "center" });

  // Photos are intentionally omitted in the dual layout — there's no room
  // and depot ops confirmed photos are rarely printed on the waybill
  // anyway. If needed they'll be added on a separate page in a follow-up.
  void photos;

  // NB: intentionally NOT calling applyMtgFooterToAllPages here — the
  // global footer collides with the bottom-copy signature strip. Each
  // copy already has its own signature block; the depot logo appears on
  // both blue banners so branding is preserved.
  return doc;
}

/**
 * Per-gate-event electronic waybill (Arkas-style Equipment Interchange Receipt).
 *
 * Renders a compact 1-page PDF for a single gate-IN or gate-OUT event so the
 * carrier's fleet manager gets a proper acknowledgement of the movement
 * (matches the ARL-Shipping format Arkas asked us to reproduce).
 */
export async function downloadGateWaybillPdf(event, photos = []) {
  const doc = await _buildGateWaybillDoc(event, photos);
  const gateType = (event.gate_type || "IN").toUpperCase();
  const dt = event.event_time ? new Date(event.event_time).toISOString().slice(0, 10) : "waybill";
  doc.save(`Waybill_${event.container_number || "container"}_${gateType}_${dt}.pdf`);
}

/**
 * Same document as ``downloadGateWaybillPdf`` but returns the PDF as a Blob
 * so callers can post it to `/gate-events/{id}/email-waybill` for server-side
 * emailing (mirrors the ``invoicePdfBlob`` pattern used by invoice emails).
 */
export async function gateWaybillPdfBlob(event, photos = []) {
  const doc = await _buildGateWaybillDoc(event, photos);
  return doc.output("blob");
}

/**
 * Cleaning Certificate PDF — matches the MTG "Certificate of Cleanliness" layout.
 */
// ─── Cleanliness & Leak-Test Certificate (combined wash + prep) ──────────
export async function downloadPrepCertificatePdf(wash, {
  depotName = "Multi Purpose Terminal Ghana Ltd. (MTG)",
  depotAddress = "Publishing Road, TEMA, GHANA",
} = {}) {
  const prep = wash.prep_package || {};
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 36;

  const fmt = (t) => t ? new Date(t).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).toUpperCase().replace(",", "") : "—";

  // ─ White logo band + lime accent stripe ─
  const logo = await loadPortsideLogo();
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageW, 80, "F");
  doc.setFillColor(153, 212, 32); // #99D420
  doc.rect(0, 80, pageW, 4, "F");

  if (logo) {
    const targetH = 56;
    const targetW = Math.round((logo.width / logo.height) * targetH);
    doc.addImage(logo.dataUrl, "PNG", margin, 12, targetW, targetH);
  } else {
    doc.setTextColor(0, 110, 158);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("PORTSIDE", margin, 36);
  }

  doc.setTextColor(0, 110, 158);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Cleanliness & Leak-Test", pageW - margin, 36, { align: "right" });
  doc.text("CERTIFICATE", pageW - margin, 54, { align: "right" });

  // Meta band
  doc.setTextColor(0, 0, 0);
  let y = 110;
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(`Cert No: ${wash.cleaning_cert_no || "DRAFT"}`, margin, y);
  doc.text(`Wash No: ${wash.wash_no || "—"}`, margin + 200, y);
  doc.text(`Issued: ${fmt(wash.certified_at || wash.completed_at || new Date().toISOString())}`, pageW - margin, y, { align: "right" });
  y += 16;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageW - margin, y);
  y += 14;

  // Tank + customer block
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Tank container", margin, y);
  doc.text("Customer", margin + 270, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Number: ${wash.tank_number}`, margin, y);
  doc.text(`Name: ${wash.customer || "—"}`, margin + 270, y);
  y += 12;
  doc.text(`Type: ${wash.iso_code || "—"} · ${wash.tank_size || "—"}`, margin, y);
  doc.text(`Attn: ${wash.customer_attn || "—"}`, margin + 270, y);
  y += 12;
  doc.text(`EIR: ${wash.eir_no || "—"}`, margin, y);
  doc.text(`Cleaned for: ${wash.cleaned_for || wash.customer || "—"}`, margin + 270, y);
  y += 22;

  // Section: Step 1 — Wash
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 110, 158);
  doc.text("Step 1 · Food-Grade Wash & Dry", margin, y);
  doc.setTextColor(0, 0, 0);
  y += 6;
  autoTable(doc, {
    startY: y + 4,
    theme: "grid",
    head: [["#", "Procedure", "EFTCO"]],
    body: (wash.cleaning_procedures || []).map((p, i) => {
      const code = PROC_CODES[p] || "";
      return [String(i + 1), p, code];
    }),
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [240, 240, 240], textColor: 30, fontStyle: "bold" },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 14;

  // Section: Step 2 — Gaskets
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 110, 158);
  doc.text("Step 2 · Mandatory Gasket Replacement", margin, y);
  doc.setTextColor(0, 0, 0);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const gasketLabel = ({ WHITE_EPDM: "White EPDM (FDA food-grade)", PTFE_ENVELOPE: "PTFE Envelope", SILICONE: "Silicone", BLACK_EPDM: "Black EPDM", VITON: "Viton" })[prep.gasket_type] || prep.gasket_type || "—";
  doc.text(`Gasket type installed: ${gasketLabel}`, margin, y); y += 12;
  doc.text(`Replaced on: ${(prep.gaskets_replaced || []).map((g) => g.replace("_", " ").toLowerCase()).join(" · ") || "—"}`, margin, y); y += 18;

  // Section: Step 3 — Pressure test
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 110, 158);
  doc.text("Step 3 · Pneumatic Leak / Pressure Test", margin, y);
  doc.setTextColor(0, 0, 0);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Test pressure: ${prep.test_pressure_bar ?? "—"} bar`, margin, y);
  // Result chip
  const result = prep.pressure_test_result || "—";
  const resultColor = result === "PASS" ? [153, 212, 32] : result === "FAIL" ? [241, 71, 29] : [128, 128, 128];
  doc.setFillColor(...resultColor);
  doc.rect(margin + 200, y - 10, 70, 16, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text(`RESULT: ${result}`, margin + 235, y + 1, { align: "center" });
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "normal");
  y += 22;

  // Section: Step 4 — Seals
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 110, 158);
  doc.text("Step 4 · Tamper-Evident Security Seals", margin, y);
  doc.setTextColor(0, 0, 0);
  y += 6;
  autoTable(doc, {
    startY: y + 4,
    theme: "grid",
    head: [["Valve / Opening", "Seal Number"]],
    body: [
      ["Manlid", prep.seal_manlid || "—"],
      ["Airline", prep.seal_airline || "—"],
      ["Bottom discharge", prep.seal_discharge || "—"],
    ],
    styles: { fontSize: 10, cellPadding: 5, font: "courier" },
    headStyles: { fillColor: [240, 240, 240], textColor: 30, fontStyle: "bold" },
    margin: { left: margin, right: margin },
    columnStyles: { 0: { cellWidth: 200 }, 1: { fontStyle: "bold" } },
  });
  y = doc.lastAutoTable.finalY + 24;

  // Statement
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.text("We certify that the tank container listed above has been cleaned to food-grade standard, that all listed", margin, y); y += 10;
  doc.text("gaskets have been replaced with new FDA-approved material, the pneumatic leak test has been performed", margin, y); y += 10;
  doc.text("and that the openings have been sealed with the numbered tamper-evident seals shown above.", margin, y);
  y += 28;

  // Signatures
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.line(margin, y, margin + 200, y);
  doc.line(pageW - margin - 200, y, pageW - margin, y);
  y += 12;
  doc.text(`Technician: ${prep.technician || "—"}`, margin, y);
  doc.text(`Quality / Supervisor`, pageW - margin - 200, y);

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(120, 120, 120);
  doc.text(`${depotName} · ${depotAddress}`, margin, doc.internal.pageSize.getHeight() - 24);
  doc.text(`Generated ${fmt(new Date().toISOString())}`, pageW - margin, doc.internal.pageSize.getHeight() - 24, { align: "right" });

  applyMtgFooterToAllPages(doc);
  doc.save(`PrepCert_${wash.wash_no || wash.tank_number}.pdf`);
}


// ─── Async logo loader (memoised) ────────────────────────────────────────
// The MTG/Portside logo is served from /portside-logo.png. We fetch it once
// then cache the dataURL + dimensions for the lifetime of the SPA session
// so subsequent certificate downloads are instant.
let _logoCache = null;
async function loadPortsideLogo() {
  if (_logoCache) return _logoCache;
  try {
    const res = await fetch("/portside-logo.png", { cache: "force-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
    const img = await new Promise((resolve) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.src = dataUrl;
    });
    _logoCache = { dataUrl, width: img.naturalWidth, height: img.naturalHeight };
    return _logoCache;
  } catch {
    return null;
  }
}


export async function downloadCleaningCertificatePdf(wash, { depotName = "Multi Purpose Terminal Ghana Ltd. (MTG)", depotAddress = "Publishing Road, TEMA, GHANA" } = {}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;

  const fmtTime = (t) => t ? new Date(t).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).toUpperCase().replace(",", "") : "—";
  const fmtDate = (t) => t ? new Date(t).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase() : "—";

  // ─ White logo band, then thin blue accent stripe ─
  const logo = await loadPortsideLogo();
  const headerH = 80;
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageW, headerH, "F");
  doc.setFillColor(0, 47, 167);
  doc.rect(0, headerH, pageW, 4, "F");

  // Logo top-left
  if (logo) {
    const targetH = 56;
    const targetW = Math.round((logo.width / logo.height) * targetH);
    doc.addImage(logo.dataUrl, "PNG", margin, 12, targetW, targetH);
  } else {
    // Fallback: text wordmark
    doc.setTextColor(0, 47, 167);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("PORTSIDE", margin, 36);
  }

  // Right-side title
  doc.setTextColor(0, 47, 167);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Certificate of Cleanliness", pageW - margin, 36, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text("Tank Wash Station · " + depotAddress, pageW - margin, 54, { align: "right" });

  doc.setTextColor(0, 0, 0);

  // ----- Depot / Customer block (left) and Document numbers (right) -----
  const topY = 110;
  const labelStyle = () => { doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(120, 120, 120); };
  const valueStyle = () => { doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(0, 0, 0); };

  labelStyle();
  doc.text("Depot:", margin, topY);
  valueStyle();
  doc.text(depotName, margin + 60, topY);
  doc.text(depotAddress, margin + 60, topY + 12);

  labelStyle();
  doc.text("Customer:", margin, topY + 32);
  valueStyle();
  doc.text(wash.customer || "—", margin + 60, topY + 32, { maxWidth: 260 });
  if (wash.customer_address) {
    doc.setFontSize(8);
    doc.text(wash.customer_address, margin + 60, topY + 44, { maxWidth: 260 });
  }
  if (wash.customer_email || wash.customer_attn) {
    doc.setFontSize(8);
    doc.text(`Email: ${wash.customer_email || "—"}    Attn: ${wash.customer_attn || "—"}`, margin + 60, topY + 56, { maxWidth: 260 });
  }

  // Right column: document numbers
  const rightX = pageW - margin - 200;
  const docRow = (label, value, y) => {
    labelStyle();
    doc.text(label, rightX, y);
    valueStyle();
    doc.setFont("helvetica", "bold");
    doc.text(String(value || "—"), rightX + 110, y);
  };
  docRow("Document No.:", wash.wash_no, topY);
  docRow("Cleaning Certificate No.:", wash.cleaning_cert_no, topY + 14);
  docRow("EIR No.:", wash.eir_no, topY + 28);
  docRow("Equipment Number:", wash.tank_number, topY + 42);

  // ----- Equipment + cargo details table -----
  const eqY = topY + 80;
  // Cargo helper: when product trade-name and chemical name are identical
  // (or one is missing), only show the populated one to avoid the appearance
  // of redundant information on the certificate.
  const fmtCargo = (product, chem, un) => {
    const p = (product || "").trim();
    const c = (chem || "").trim();
    const u = (un || "").trim();
    if (!p && !c) return "—";
    if (!c || p.toUpperCase() === c.toUpperCase()) {
      return u ? `${p || c} (UN ${u})` : (p || c);
    }
    if (!p) return u ? `${c} (UN ${u})` : c;
    return u ? `${p} — ${c} (UN ${u})` : `${p} — ${c}`;
  };

  autoTable(doc, {
    startY: eqY,
    head: [["Equipment & Cargo Details", ""]],
    body: [
      ["Last product carried",     fmtCargo(wash.last_cargo,   wash.last_cargo_chem_name,   wash.last_cargo_un_no)],
      ["Last product carried II",  fmtCargo(wash.last_cargo_2, wash.last_cargo_chem_name_2, wash.last_cargo_un_no_2)],
      ["Last product carried III", fmtCargo(wash.last_cargo_3, wash.last_cargo_chem_name_3, wash.last_cargo_un_no_3)],
      ["IMO Class", wash.imo_class || "—"],
      ["Place of Cleaning", wash.place_of_cleaning || "—"],
      ["Cleaned For", wash.cleaned_for || "—"],
      ["ISO Code", wash.iso_code || "—"],
      ["Type", wash.tank_type || "—"],
      ["Size", wash.tank_size || "—"],
      ["Cleaning Date", fmtTime(wash.cleaning_date)],
      ["Inspection Date", fmtTime(wash.inspection_date)],
    ],
    headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 160, fontStyle: "bold", textColor: [80, 80, 80] }, 1: { cellWidth: "auto" } },
    margin: { left: margin, right: margin },
  });

  let y = doc.lastAutoTable.finalY + 16;

  // ----- Cleaning Procedure -----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 47, 167);
  doc.text("Cleaning Procedure", margin, y);
  y += 6;
  doc.setDrawColor(0, 47, 167);
  doc.setLineWidth(1);
  doc.line(margin, y, margin + 180, y);
  y += 12;

  const procs = wash.cleaning_procedures || [];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  if (procs.length === 0) {
    doc.text("— no procedures recorded —", margin, y);
    y += 14;
  } else {
    procs.forEach((p, i) => {
      const code = codeFor(p);
      if (code) {
        doc.setFont("courier", "bold");
        doc.setTextColor(241, 71, 29);
        doc.text(code, margin, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        doc.text(`— ${p}`, margin + 34, y);
      } else {
        doc.text(`${i + 1}.  ${p}`, margin, y);
      }
      y += 14;
    });
  }

  // ----- Seal Numbers -----
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 47, 167);
  doc.text("Seal Numbers:", margin, y);
  y += 6;
  doc.line(margin, y, margin + 180, y);
  y += 14;

  const seals = wash.seals || {};
  const breaks = Array.isArray(wash.seal_breaks) ? wash.seal_breaks : [];
  const breaksByPosition = breaks.reduce((acc, b) => {
    (acc[b.position] = acc[b.position] || []).push(b);
    return acc;
  }, {});
  const sealRows = [
    ["1. Man lid Seal No.", seals.man_lid || "—", "man_lid"],
    ["2. Top Discharge valve Seal No.", seals.top_discharge_valve || "—", "top_discharge_valve"],
    ["3. Airline Seal No.", seals.airline || "—", "airline"],
    ["4. Bottom Outlet Seal No.", seals.bottom_outlet || "—", "bottom_outlet"],
  ];
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  sealRows.forEach(([label, val, pos]) => {
    doc.text(label, margin, y);
    doc.setFont("helvetica", "bold");
    doc.text(String(val), margin + 200, y);
    if (breaksByPosition[pos]?.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(176, 24, 24);
      doc.text("* see seal break record below", margin + 280, y);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
    }
    doc.setFont("helvetica", "normal");
    y += 14;
  });

  // ----- Seal break record (only when there's at least one) -----
  if (breaks.length) {
    y += 8;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(176, 24, 24);
    doc.text("Seal Break Record", margin, y);
    y += 4;
    doc.line(margin, y, margin + 180, y);
    y += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    const POS_LABEL = {
      man_lid: "Man-lid",
      top_discharge_valve: "Top discharge valve",
      airline: "Airline",
      bottom_outlet: "Bottom outlet",
    };
    breaks.forEach((b, i) => {
      const when = b.broken_at ? new Date(b.broken_at).toLocaleDateString("en-GB") : "—";
      const tech = b.broken_by || "—";
      const pos = POS_LABEL[b.position] || b.position;
      doc.setFont("helvetica", "bold");
      doc.text(`${i + 1}. ${pos}`, margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Broken on ${when} by ${tech}.  Original seal: ${b.original_seal || "—"}` +
          (b.replacement_seal ? `  →  replaced with: ${b.replacement_seal}` : "  (no replacement applied)"),
        margin + 110, y
      );
      y += 12;
      const reasonText = doc.splitTextToSize(`Reason: ${b.reason}`, 380);
      reasonText.forEach((line) => { doc.text(line, margin + 110, y); y += 11; });
      y += 4;
    });
  }

  // ----- ATP Hygiene Test (only when recorded) -----
  if (wash.atp_rlu !== undefined && wash.atp_rlu !== null) {
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 47, 167);
    doc.text("ATP Hygiene Test", margin, y);
    y += 6;
    doc.line(margin, y, margin + 180, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const atpRows = [
      ["Reading (RLU)", String(wash.atp_rlu)],
      ["Threshold (≤ pass)", String(wash.atp_threshold || 30) + " RLU"],
      ["Result", wash.atp_pass ? "PASS" : "FAIL"],
      ["Tested By", wash.atp_tested_by || "—"],
      ["Tested At", fmtTime(wash.atp_tested_at)],
    ];
    atpRows.forEach(([label, val]) => {
      doc.text(label, margin, y);
      doc.setFont("helvetica", "bold");
      // Colour the result row green/red for at-a-glance review
      if (label === "Result") {
        doc.setTextColor(wash.atp_pass ? 22 : 192, wash.atp_pass ? 128 : 30, wash.atp_pass ? 60 : 30);
      }
      doc.text(String(val), margin + 200, y);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      y += 14;
    });
  }

  // ----- Disclaimer -----
  y += 20;
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(
    "This report refers to the condition of the above equipment at the time and place of inspection only.",
    margin, y, { maxWidth: pageW - margin * 2 }
  );

  // ----- Footer -----
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Updated By: ${wash.updated_by || wash.created_by || "—"}`, margin, pageH - 50);
  doc.text(`Generated By: ${wash.created_by || "—"}`, margin, pageH - 38);
  doc.text(`Generation Date: ${fmtDate(new Date())}`, margin, pageH - 26);
  doc.setFontSize(7);
  doc.text(
    "This is a computer-generated document. No signature is required.",
    pageW - margin, pageH - 26, { align: "right" }
  );

  // Use wash_no for the filename when the cert no doesn't look like our MTG-…
  // format (e.g. for HISTORICAL Excel imports that carry a raw external ref).
  const fname = (wash.cleaning_cert_no && /^MTG-/i.test(wash.cleaning_cert_no))
    ? wash.cleaning_cert_no
    : wash.wash_no;
  applyMtgFooterToAllPages(doc);
  doc.save(`${fname}.pdf`);
}

/**
 * Wash WORK ORDER PDF — the cleaner's printable job ticket.
 * Lists tasks to perform (Kosher, Halal, Hot-water spin, Caustic wash, etc.),
 * shows tank ID / customer / last cargo / seals, and includes signature lines
 * for the cleaner and supervisor to sign off when the wash is complete.
 *
 * Issued BEFORE the wash starts. The Cleaning Certificate is issued AFTER.
 */
export async function downloadWashWorkOrderPdf(wash) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;

  const fmtTime = (t) =>
    t
      ? new Date(t).toLocaleString("en-GB", {
          day: "2-digit", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit", hour12: false,
        }).toUpperCase().replace(",", "")
      : "—";

  // MTG letterhead
  await drawMtgHeader(doc, { title: "Wash Work Order", subtitle: "Cleaner's job ticket", accent: "lime" });

  // 4-color brand rule
  const rule = [
    [0, 110, 158, 0.40],   // blue
    [0, 141, 198, 0.20],   // blue light
    [153, 212, 32, 0.20],  // lime
    [241, 71, 29, 0.20],   // orange
  ];
  let rx = 0;
  rule.forEach(([r, g, b, frac]) => {
    doc.setFillColor(r, g, b);
    doc.rect(rx, 66, pageW * frac, 4, "F");
    rx += pageW * frac;
  });

  doc.setTextColor(0, 0, 0);

  // ----- Header info block (Tank + Category + Doc numbers) -----
  let y = 92;
  const colW = (pageW - margin * 2) / 3;

  const block = (x, label, value, opts = {}) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(label.toUpperCase(), x, y);
    doc.setFont("helvetica", opts.mono ? "courier" : "bold");
    doc.setFontSize(opts.large ? 14 : 11);
    doc.setTextColor(0, 0, 0);
    doc.text(String(value || "—"), x, y + 16);
  };
  block(margin, "Tank Number", wash.tank_number, { mono: true, large: true });
  block(margin + colW, "Work Order No.", wash.wash_no, { mono: true });
  block(margin + colW * 2, "Category", wash.wash_category || "—");
  y += 36;

  // Divider
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageW - margin, y);
  y += 14;

  // ----- Customer + Tank Info row -----
  const labelStyle = () => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
  };
  const valueStyle = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
  };

  const leftX = margin;
  const rightX = margin + (pageW - margin * 2) / 2 + 10;
  let leftY = y;
  let rightY = y;

  const rowL = (label, value) => {
    labelStyle();
    doc.text(label.toUpperCase(), leftX, leftY);
    valueStyle();
    doc.text(String(value || "—"), leftX, leftY + 12, { maxWidth: (pageW - margin * 2) / 2 - 10 });
    leftY += 28;
  };
  const rowR = (label, value) => {
    labelStyle();
    doc.text(label.toUpperCase(), rightX, rightY);
    valueStyle();
    doc.text(String(value || "—"), rightX, rightY + 12, { maxWidth: (pageW - margin * 2) / 2 - 10 });
    rightY += 28;
  };

  rowL("Customer", wash.customer);
  rowL("Cleaned For", wash.cleaned_for);
  rowL("EIR No.", wash.eir_no);

  rowR("ISO Type", wash.iso_code);
  rowR("Tank Size", wash.tank_size);
  rowR("Issued", fmtTime(wash.created_at));

  y = Math.max(leftY, rightY) + 4;

  // ----- Cargo history -----
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, pageW - margin, y);
  y += 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 49, 71);
  doc.text("LAST CARGO HISTORY", margin, y);
  y += 14;

  const cargos = [
    { label: "Last cargo", name: wash.last_cargo_chem_name || wash.last_cargo, un: wash.last_cargo_un_no },
    { label: "Cargo II", name: wash.last_cargo_chem_name_2 || wash.last_cargo_2, un: wash.last_cargo_un_no_2 },
    { label: "Cargo III", name: wash.last_cargo_chem_name_3 || wash.last_cargo_3, un: wash.last_cargo_un_no_3 },
  ];

  autoTable(doc, {
    startY: y,
    head: [["#", "Cargo / Chemical name", "UN No."]],
    body: cargos.map((c, i) => [
      c.label,
      c.name || "—",
      c.un || "—",
    ]),
    headStyles: { fillColor: [255, 251, 235], textColor: 70, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 70, fontStyle: "bold" }, 2: { cellWidth: 90, font: "courier" } },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 14;

  // ----- TASKS TO PERFORM (the heart of the work order) -----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 49, 71);
  doc.text("TASKS TO PERFORM", margin, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text("Tick each task when complete.", margin + 124, y);
  y += 6;

  // Coloured rule under the heading
  doc.setFillColor(241, 71, 29);
  doc.rect(margin, y, 36, 2, "F");
  doc.setFillColor(153, 212, 32);
  doc.rect(margin + 38, y, 36, 2, "F");
  y += 14;

  const procedures = Array.isArray(wash.cleaning_procedures) && wash.cleaning_procedures.length
    ? wash.cleaning_procedures
    : ["(no procedures specified — confirm with supervisor before starting)"];

  // Each task = checkbox + label + signature line for the cleaner to initial
  doc.setTextColor(0, 0, 0);
  procedures.forEach((p, i) => {
    const lineY = y + i * 26;
    // Box
    doc.setDrawColor(0, 49, 71);
    doc.setLineWidth(1);
    doc.rect(margin, lineY - 9, 12, 12);
    // Task label — prefix EFTCO code in bold if available
    const code = codeFor(p);
    doc.setFontSize(10);
    if (code) {
      doc.setFont("courier", "bold");
      doc.setTextColor(241, 71, 29);
      doc.text(code, margin + 22, lineY);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text(`— ${p}`, margin + 56, lineY);
    } else {
      doc.setFont("helvetica", "bold");
      doc.text(`${i + 1}. ${p}`, margin + 22, lineY);
    }
    // Initial line
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.5);
    doc.line(pageW - margin - 120, lineY + 2, pageW - margin, lineY + 2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    doc.text("Initials", pageW - margin - 120, lineY + 12);
    doc.setTextColor(140, 140, 140);
    doc.text("Time:", pageW - margin - 60, lineY + 12);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
  });
  y += procedures.length * 26 + 8;

  // ----- Seals on completion -----
  if (y > pageH - 240) { doc.addPage(); y = margin; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 49, 71);
  doc.text("SEALS ON COMPLETION", margin, y);
  y += 10;
  doc.setFillColor(0, 110, 158);
  doc.rect(margin, y, 36, 2, "F");
  y += 12;

  const seals = wash.seals || {};
  const sealRows = [
    ["Man-lid seal", seals.man_lid || ""],
    ["Top discharge valve seal", seals.top_discharge_valve || ""],
    ["Airline seal", seals.airline || ""],
    ["Bottom outlet seal", seals.bottom_outlet || ""],
  ];
  autoTable(doc, {
    startY: y,
    head: [["Seal location", "Seal number (fill in)"]],
    body: sealRows,
    headStyles: { fillColor: [255, 251, 235], textColor: 70, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 9, cellPadding: 6, minCellHeight: 22 },
    columnStyles: { 0: { cellWidth: 200, fontStyle: "bold" }, 1: { font: "courier" } },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 16;

  // ----- Notes box -----
  if (y > pageH - 200) { doc.addPage(); y = margin; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 49, 71);
  doc.text("OBSERVATIONS / NOTES", margin, y);
  y += 10;
  doc.setFillColor(153, 212, 32);
  doc.rect(margin, y, 36, 2, "F");
  y += 8;

  doc.setDrawColor(200, 200, 200);
  doc.rect(margin, y, pageW - margin * 2, 70);
  // Faint guide lines
  for (let i = 1; i < 4; i++) {
    doc.setDrawColor(235, 235, 235);
    doc.line(margin + 6, y + i * 17, pageW - margin - 6, y + i * 17);
  }
  if (wash.notes) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`(pre-filled note: ${wash.notes})`, margin + 6, y + 12, { maxWidth: pageW - margin * 2 - 12 });
  }
  y += 80;

  // ----- Sign-off block -----
  if (y > pageH - 130) { doc.addPage(); y = margin; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 49, 71);
  doc.text("SIGN-OFF", margin, y);
  y += 10;
  doc.setFillColor(241, 71, 29);
  doc.rect(margin, y, 36, 2, "F");
  y += 24;

  const signColW = (pageW - margin * 2 - 24) / 2;
  const signBox = (x, role) => {
    doc.setDrawColor(0, 49, 71);
    doc.setLineWidth(0.5);
    doc.line(x, y + 36, x + signColW, y + 36);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(role.toUpperCase(), x, y + 48);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("Name (print):", x, y + 64);
    doc.line(x + 60, y + 64, x + signColW, y + 64);
    doc.text("Date / time:", x, y + 80);
    doc.line(x + 60, y + 80, x + signColW, y + 80);
  };
  signBox(margin, "Cleaner — work completed");
  signBox(margin + signColW + 24, "Supervisor — work approved");

  // ----- Footer -----
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `${MTG_BRAND.name} · Work order ${wash.wash_no} · Issued ${fmtTime(wash.created_at)} · Sign and return to operations once the wash is complete`,
    pageW / 2,
    pageH - 18,
    { align: "center", maxWidth: pageW - 36 }
  );

  applyMtgFooterToAllPages(doc);
  doc.save(`workorder_${wash.wash_no}.pdf`);
}




/**
 * Generic Report PDF — branded Portside header, filter chip strip,
 * data table with autoTable, optional totals strip below.
 *
 *   downloadReportPdf({
 *     title: "Gate Events Report",
 *     filename: "gate_events_2026-04-01_2026-05-15",
 *     filters: [{ label: "Period", value: "01 Apr 2026 → 15 May 2026" }, ...],
 *     columns: [{ header: "Container", key: "container_number" }, ...],
 *     rows: [...],
 *     totals: [{ label: "Events", value: 1234 }, ...],
 *     orientation: "portrait" | "landscape",
 *   })
 */
export async function downloadReportPdf({
  title,
  filename,
  filters = [],
  columns = [],
  rows = [],
  totals = [],
  orientation = "portrait",
  subtitle,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 36;

  // MTG letterhead
  await drawMtgHeader(doc, {
    title: title.toUpperCase(),
    subtitle: subtitle || `Generated ${new Date().toLocaleString("en-GB")}`,
    accent: "blue",
  });

  // Filter chip strip
  let y = 110;
  if (filters.length) {
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text("FILTERS", margin, y);
    y += 6;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 12;
    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "normal");
    filters.forEach((f) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text(f.label + ":", margin, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(f.value ?? "—"), margin + 64, y);
      y += 13;
    });
    y += 6;
  }

  // Data table
  autoTable(doc, {
    startY: y,
    head: [columns.map((c) => c.header)],
    body: rows.map((r) => columns.map((c) => {
      const v = c.format ? c.format(r[c.key], r) : r[c.key];
      return v == null ? "" : String(v);
    })),
    headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 7.5, cellPadding: 3.5, overflow: "linebreak" },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.width || c.align || c.mono) {
        acc[i] = {};
        if (c.width) acc[i].cellWidth = c.width;
        if (c.align) acc[i].halign = c.align;
        if (c.mono) acc[i].font = "courier";
      }
      return acc;
    }, {}),
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      doc.setFontSize(7);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${doc.internal.getNumberOfPages()} · ${title} · MTG (Portside group)`,
        pageW - margin,
        doc.internal.pageSize.getHeight() - 14,
        { align: "right" }
      );
    },
  });

  // Totals strip
  if (totals.length) {
    const endY = (doc.lastAutoTable?.finalY || y) + 16;
    const boxW = Math.min(360, pageW - margin * 2);
    const boxX = pageW - margin - boxW;
    doc.setDrawColor(220, 220, 220);
    doc.rect(boxX, endY, boxW, 16 + totals.length * 14);

    doc.setFontSize(8);
    totals.forEach((t, i) => {
      const yy = endY + 16 + i * 14;
      doc.setTextColor(110, 110, 110);
      doc.setFont("helvetica", "normal");
      doc.text(t.label, boxX + 10, yy);
      doc.setTextColor(0, 0, 0);
      doc.setFont("courier", t.bold ? "bold" : "normal");
      doc.text(String(t.value), boxX + boxW - 10, yy, { align: "right" });
    });
  }

  applyMtgFooterToAllPages(doc);
  doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}


// ────────────────────────────────────────────────────────────
// REPAIR ESTIMATE PDF — styled to match the Cleaning Certificate
// ────────────────────────────────────────────────────────────
export async function downloadRepairEstimatePdf(estimate) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;
  const ccy = estimate.currency || "USD";
  const t = estimate.totals || {};

  const fmtDate = (val) =>
    val ? new Date(val).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase() : "—";

  // MTG letterhead
  await drawMtgHeader(doc, {
    title: "Repair Estimate",
    subtitle: `Tank Container Repair · ${estimate.estimate_no || ""}`,
    accent: "blue",
  });

  // ----- Depot / Customer block (left) and Document numbers (right) -----
  const topY = 110;
  const labelStyle = () => { doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(120, 120, 120); };
  const valueStyle = () => { doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(0, 0, 0); };

  labelStyle();
  doc.text("Depot:", margin, topY);
  valueStyle();
  doc.text(MTG_BRAND.name, margin + 60, topY);
  doc.text(MTG_BRAND.address, margin + 60, topY + 12);
  doc.setFontSize(8).setTextColor(80, 80, 80);
  doc.text(`Tel: ${MTG_BRAND.phones[0]} / ${MTG_BRAND.phones[1]}`, margin + 60, topY + 24);

  labelStyle();
  doc.text("Customer:", margin, topY + 46);
  valueStyle();
  doc.text(estimate.customer || "—", margin + 60, topY + 46, { maxWidth: 260 });
  if (estimate.customer_address) {
    doc.setFontSize(8);
    doc.text(estimate.customer_address, margin + 60, topY + 58, { maxWidth: 260 });
  }
  if (estimate.customer_email || estimate.customer_attn) {
    doc.setFontSize(8);
    doc.text(
      `Email: ${estimate.customer_email || "—"}    Attn: ${estimate.customer_attn || "—"}`,
      margin + 60, topY + 70, { maxWidth: 260 }
    );
  }

  // Right column: document numbers
  const rightX = pageW - margin - 200;
  const docRow = (label, value, y) => {
    labelStyle();
    doc.text(label, rightX, y);
    valueStyle();
    doc.setFont("helvetica", "bold");
    doc.text(String(value || "—"), rightX + 110, y);
  };
  const issuedAt = estimate.created_at ? new Date(estimate.created_at) : new Date();
  docRow("Estimate No.:",   estimate.estimate_no, topY);
  docRow("Equipment No.:",  estimate.container_number, topY + 14);
  docRow("Status:",         estimate.status, topY + 28);
  docRow("Issue Date:",     fmtDate(issuedAt), topY + 42);
  docRow("Labour Tariff:",  `${ccy} ${Number(estimate.labor_rate || 0).toFixed(2)}/hr`, topY + 56);
  docRow("Issued By:",      estimate.created_by || "system", topY + 70);

  // ----- Repair line items table -----
  // Compute per-line labour and material subtotals so the row math adds up
  // by inspection. Avoids the "1.25 rounded to 1.3" optical-error trap.
  const tableY = topY + 100;
  const fmtHrs = (h) => {
    const n = Number(h || 0);
    return Number.isInteger(n) ? n.toFixed(1) : n.toFixed(2);
  };
  const body = (estimate.lines || []).map((l, i) => {
    const qty = Number(l.qty || 1);
    const hrs = Number(l.labor_hours || 0);
    const rate = Number(l.labor_rate || 0);
    const matEach = Number(l.material_cost || 0);
    const labSub = qty * hrs * rate;
    const matSub = qty * matEach;
    return [
      String(i + 1),
      l.item_code || "—",
      l.description,
      l.damage_code || "",
      l.location || "",
      String(qty),
      fmtHrs(hrs),
      fmtMoney(rate, ccy),
      fmtMoney(labSub, ccy),
      fmtMoney(matSub, ccy),
      fmtMoney(l.total ?? (labSub + matSub), ccy),
    ];
  });

  autoTable(doc, {
    startY: tableY,
    head: [["#", "Code", "Description", "Dam", "Loc", "Qty", "Hrs", "Rate/h", "Labour", "Material", "Total"]],
    body,
    headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 4 },
    columnStyles: {
      0:  { cellWidth: 18, halign: "center" },
      1:  { cellWidth: 60, font: "courier" },
      2:  { cellWidth: "auto" },
      3:  { cellWidth: 26, halign: "center", font: "courier" },
      4:  { cellWidth: 28, halign: "center", font: "courier" },
      5:  { cellWidth: 22, halign: "right" },
      6:  { cellWidth: 30, halign: "right", font: "courier" },
      7:  { cellWidth: 44, halign: "right", font: "courier" },
      8:  { cellWidth: 48, halign: "right", font: "courier" },
      9:  { cellWidth: 48, halign: "right", font: "courier" },
      10: { cellWidth: 54, halign: "right", font: "courier", fontStyle: "bold" },
    },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8).setTextColor(150, 150, 150);
      doc.text(str, pageW - margin, pageH - 20, { align: "right" });
    },
  });

  // ----- Totals box (refined card) -----
  const endY = doc.lastAutoTable.finalY + 20;
  const boxX = pageW - margin - 220;
  doc.setDrawColor(220, 220, 220).rect(boxX, endY, 220, 80);
  doc.setFontSize(9).setTextColor(80, 80, 80);
  doc.text("Total labour", boxX + 10, endY + 18);
  doc.text("Total material", boxX + 10, endY + 34);
  doc.text("Line items", boxX + 10, endY + 52);
  doc.setTextColor(0, 0, 0).setFont("courier", "normal");
  doc.text(fmtMoney(t.total_labor, ccy), boxX + 210, endY + 18, { align: "right" });
  doc.text(fmtMoney(t.total_material, ccy), boxX + 210, endY + 34, { align: "right" });
  doc.text(String(t.line_items ?? 0), boxX + 210, endY + 52, { align: "right" });

  doc.setFillColor(0, 47, 167);
  doc.rect(boxX, endY + 60, 220, 22, "F");
  doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(10);
  doc.text("GRAND TOTAL", boxX + 10, endY + 75);
  doc.setFont("courier", "bold");
  doc.text(fmtMoney(t.grand_total, ccy), boxX + 210, endY + 75, { align: "right" });

  // ----- Authorization block (same content as cleaning cert footer pattern) -----
  const authY = endY + 110;
  doc.setTextColor(0, 47, 167).setFont("helvetica", "bold").setFontSize(11);
  doc.text("Authorization", margin, authY);
  doc.setDrawColor(0, 47, 167);
  doc.line(margin, authY + 4, margin + 88, authY + 4);

  doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(60, 60, 60);
  const note =
    "An authorization for this estimate is required within 5 days after the issue date, " +
    "duly signed by an approved company official. After authorization we will commence with the repairs " +
    "in order to make the tank container available to you soonest. All gaskets are renewed by default unless " +
    "the customer requests otherwise.";
  const wrapped = doc.splitTextToSize(note, pageW - 2 * margin);
  doc.text(wrapped, margin, authY + 22);

  doc.setDrawColor(180, 180, 180);
  doc.line(margin, authY + 80, margin + 220, authY + 80);
  doc.line(pageW - margin - 220, authY + 80, pageW - margin, authY + 80);
  doc.setFontSize(8).setTextColor(120, 120, 120);
  doc.text("Customer signature & date", margin, authY + 92);
  doc.text("MTG authorised signatory", pageW - margin - 220, authY + 92);

  // ----- Footer (matches cleaning cert tone) -----
  doc.setTextColor(150, 150, 150).setFontSize(7);
  doc.text(
    "Generated by MTG Operations · System-issued repair estimate, ref " + estimate.estimate_no,
    margin,
    pageH - 18
  );

  applyMtgFooterToAllPages(doc);
  doc.save(`${estimate.estimate_no}.pdf`);
}

// ────────────────────────────────────────────────────────────
// ONHIRE / OFFHIRE INSPECTION PDF
// ────────────────────────────────────────────────────────────
export async function downloadInspectionPdf(inspection) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  const title = inspection.inspection_type === "OFFHIRE" ? "OFFHIRE REPORT" : "ONHIRE REPORT";
  await drawMtgHeader(doc, { title, subtitle: inspection.inspection_no, accent: "blue" });

  // Meta block
  const metaY = 100;
  doc.setFontSize(8).setTextColor(120, 120, 120);
  doc.text("CUSTOMER", margin, metaY);
  doc.text("CONTAINER", margin + 200, metaY);
  doc.text("SURVEY DATE", pageW - margin - 120, metaY);
  doc.setTextColor(0, 0, 0).setFont("helvetica", "bold").setFontSize(11);
  doc.text(inspection.customer || "—", margin, metaY + 16);
  doc.setFont("helvetica", "normal").setFontSize(9);
  doc.text(inspection.depot || MTG_BRAND.address, margin, metaY + 30);
  doc.text(`Surveyor: ${inspection.surveyor || "—"}`, margin, metaY + 44);

  doc.setFont("courier", "bold").setFontSize(13).text(inspection.container_number, margin + 200, metaY + 18);
  doc.setFont("helvetica", "normal").setFontSize(9);
  doc.text(`ISO: ${inspection.iso_type || "—"}`, margin + 200, metaY + 32);

  doc.setFont("courier", "normal").setFontSize(11);
  doc.text(
    (inspection.survey_date ? new Date(inspection.survey_date).toISOString().slice(0, 10) : "—"),
    pageW - margin - 120, metaY + 16
  );

  // Tank specs table
  const specs = [
    ["Manufacturer", inspection.manufacturer || "—", "Tare (kg)", inspection.tare_weight_kg ?? "—"],
    ["Manu Date", inspection.manu_date || "—", "Max Gross (kg)", inspection.max_gross_weight_kg ?? "—"],
    ["Manu Serial", inspection.manu_serial || "—", "Capacity (L)", inspection.capacity_liters ?? "—"],
    ["CSC Due", inspection.csc_due || "—", "MAWP Shell (bar)", inspection.mawp_shell_bar ?? "—"],
  ];
  autoTable(doc, {
    startY: metaY + 70,
    head: [["Field", "Value", "Field", "Value"]],
    body: specs.map((row) => row.map(String)),
    headStyles: { fillColor: [11, 18, 32], textColor: 255, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 4 },
    columnStyles: {
      0: { cellWidth: 90, fontStyle: "bold", textColor: [80, 80, 80] },
      1: { cellWidth: 160, font: "courier" },
      2: { cellWidth: 100, fontStyle: "bold", textColor: [80, 80, 80] },
      3: { cellWidth: "auto", font: "courier" },
    },
    margin: { left: margin, right: margin },
  });

  // Damages
  const damages = inspection.damages || [];
  if (damages.length) {
    const dmgBody = damages.map((d, i) => [
      String(i + 1),
      d.damage_code || "—",
      d.location || "—",
      d.description || "",
      d.photo_count ? `${d.photo_count} photo${d.photo_count > 1 ? "s" : ""}` : "",
    ]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [["#", "Dam", "Loc", "Description", "Attachments"]],
      body: dmgBody,
      headStyles: { fillColor: [188, 30, 30], textColor: 255, fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 22, halign: "center" },
        1: { cellWidth: 36, halign: "center", font: "courier", fontStyle: "bold" },
        2: { cellWidth: 42, halign: "center", font: "courier" },
        3: { cellWidth: "auto" },
        4: { cellWidth: 80, font: "courier", textColor: [80, 80, 80] },
      },
      margin: { left: margin, right: margin },
    });
  } else {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      head: [["Damages"]],
      body: [["No damages recorded."]],
      headStyles: { fillColor: [99, 168, 39], textColor: 255, fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 4, halign: "center", textColor: [120, 120, 120] },
      margin: { left: margin, right: margin },
    });
  }

  // Global remarks
  if (inspection.global_remarks) {
    const y = doc.lastAutoTable.finalY + 20;
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(0, 0, 0);
    doc.text("GLOBAL REMARKS", margin, y);
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(60, 60, 60);
    const wrapped = doc.splitTextToSize(inspection.global_remarks, pageW - 2 * margin);
    doc.text(wrapped, margin, y + 14);
  }

  // Signature block
  const sigY = pageH - 80;
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, sigY, margin + 220, sigY);
  doc.line(pageW - margin - 220, sigY, pageW - margin, sigY);
  doc.setFontSize(8).setTextColor(120, 120, 120);
  doc.text("Surveyor signature", margin, sigY + 12);
  doc.text("Depot authorised signatory", pageW - margin - 220, sigY + 12);
  doc.setFontSize(7);
  doc.text(
    "This is a system-generated inspection report from MTG (a Portside group company).",
    margin,
    pageH - 20
  );

  applyMtgFooterToAllPages(doc);
  doc.save(`${inspection.inspection_no}.pdf`);
}

/* ---------------------------------------------------------------------------
 * Quick-reference Gate Guide — single-page A4 PDF that a depot supervisor can
 * print and pin at the gate kiosk. Covers Gate-In and Gate-Out side by side.
 * ------------------------------------------------------------------------- */
export function downloadGateGuidePdf() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // ---- Header band ----
  doc.setFillColor(0, 86, 122);
  doc.rect(0, 0, pageW, 68, "F");
  doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(20);
  doc.text("Gate Operations — Quick Guide", margin, 32);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text("Portside Operations · MTG Tema · Gate-IN and Gate-OUT procedures", margin, 50);
  doc.setFontSize(8).setTextColor(220, 235, 245);
  doc.text(`Printed ${new Date().toLocaleDateString()}`, pageW - margin, 50, { align: "right" });

  doc.setTextColor(60, 60, 60).setFontSize(9).setFont("helvetica", "normal");
  doc.text(
    "Every container that crosses the depot gate must be recorded as a Gate Event. The system auto-generates the EIR\nand the CODECO EDI message — never write tickets by hand.",
    margin, 90
  );

  // ---- Two-column layout ----
  const colW = (pageW - margin * 2 - 20) / 2;
  const colLeftX  = margin;
  const colRightX = margin + colW + 20;
  const colTop    = 130;

  const drawColumn = (x, accent, badge, title, intro, steps, tips) => {
    // Accent header bar
    doc.setFillColor(...accent);
    doc.rect(x, colTop, colW, 26, "F");
    doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(12);
    doc.text(`${badge}  ${title}`, x + 10, colTop + 17);

    // Intro line
    doc.setTextColor(80, 80, 80).setFont("helvetica", "italic").setFontSize(8.5);
    doc.text(intro, x + 2, colTop + 42, { maxWidth: colW - 4 });

    // Numbered steps
    let y = colTop + 64;
    doc.setFont("helvetica", "normal").setFontSize(9.5).setTextColor(30, 30, 30);
    steps.forEach((step, i) => {
      // Number disc
      doc.setFillColor(...accent);
      doc.circle(x + 9, y - 3, 7, "F");
      doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(8.5);
      doc.text(String(i + 1), x + 9, y, { align: "center" });
      // Step body
      doc.setTextColor(30, 30, 30).setFont("helvetica", "normal").setFontSize(9.5);
      const lines = doc.splitTextToSize(step, colW - 26);
      doc.text(lines, x + 22, y);
      y += Math.max(lines.length * 12, 14) + 4;
    });

    // Tips panel
    y += 6;
    doc.setDrawColor(220, 220, 220).setFillColor(250, 248, 235);
    const tipsLines = tips.flatMap((t) => doc.splitTextToSize(`•  ${t}`, colW - 16));
    const tipsH = tipsLines.length * 11 + 22;
    doc.roundedRect(x, y, colW, tipsH, 3, 3, "FD");
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(140, 90, 0);
    doc.text("Watch-outs", x + 8, y + 14);
    doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(60, 50, 20);
    doc.text(tipsLines, x + 8, y + 26);
  };

  // ============ GATE-IN ============
  drawColumn(
    colLeftX,
    [0, 110, 158],            // Portside blue
    "IN",
    "Container ARRIVING at the depot",
    "Use Gate-IN when an empty container is being dropped off by a trucker for storage, wash, or repair.",
    [
      "Click the floating + button (any screen) and choose Gate-IN, or open Gate Activity and press New gate event.",
      "Scan or type the container number. The OCR camera button works for printed numbers — verify all 11 characters.",
      "Pick the Carrier from the dropdown (HLC, MEEBERG, ARKAS, TRIDENT, …). This drives the CODECO EDI recipient.",
      "Capture the grade and condition (A / B / C / damaged). Add photos if condition is anything below A.",
      "Confirm trucker, plate number and seal (if any). Save — the EIR and EDI are created automatically.",
    ],
    [
      "If the container is already in the register, the system will reject a duplicate IN until it is gated out first.",
      "ISO Tank? After save, the system asks if a wash is required — say Yes to push it straight to the Wash queue.",
      "Damaged tank? Tick the Repair flag during save so it routes to Tank Repairs instead of empty stock.",
    ]
  );

  // ============ GATE-OUT ============
  drawColumn(
    colRightX,
    [241, 71, 29],            // Portside orange
    "OUT",
    "Container LEAVING the depot",
    "Use Gate-OUT when a container is released to a trucker for delivery to the line, customer, or another depot.",
    [
      "On Containers in the yard or Gate Activity, find the container — use the search box or scan the plate.",
      "Click Gate OUT on the row (or open the detail page and press the Gate-OUT button).",
      "Confirm trucker name, plate and out-bound destination. Add the booking / release reference if you have one.",
      "Attach the seal number applied at release. The system will warn if the unit hasn't been washed when required.",
      "Save — the system closes the EIR, ends rental/storage clocks, and emits the corresponding CODECO EDI.",
    ],
    [
      "A LOLO charge is auto-generated against the carrier tariff — review on the Invoices page before billing.",
      "Tank rental: gating out a leased tank ends its rental period for the assigned customer automatically.",
      "EDI failed to send? Open EDI Messages > Pending and press Resend. The audit log records every retry.",
    ]
  );

  // ---- Footer band with role notes ----
  const footY = pageH - 78;
  doc.setDrawColor(230, 230, 230);
  doc.line(margin, footY - 6, pageW - margin, footY - 6);
  doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(0, 86, 122);
  doc.text("Who can do what?", margin, footY + 6);
  doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(60, 60, 60);
  const roleLines = [
    "Operator — creates and edits Gate-IN / Gate-OUT events, wash orders, EIRs.",
    "Supervisor — operator powers + cancel events, resend failed EDI, view audit log.",
    "Depot Manager / GM — full operations oversight, including invoicing and EIR sign-off.",
    "Admin — user management, tariff CRUD, deletion of events / invoices / EDI recipients.",
  ];
  roleLines.forEach((l, i) => doc.text(l, margin, footY + 20 + i * 11));

  doc.setFontSize(7).setTextColor(140, 140, 140);
  doc.text(
    "Generated by Portside Operations · Tema, Ghana · The in-app User Manual (Help > User Manual) covers the full workflow in detail.",
    margin, pageH - 18
  );

  // =========================================================================
  // PAGE 2 — Visual reference (screenshots of the actual UI)
  // =========================================================================
  doc.addPage();

  // Header band
  doc.setFillColor(0, 86, 122);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(16);
  doc.text("Gate Operations — Visual Reference", margin, 28);
  doc.setFont("helvetica", "normal").setFontSize(9);
  doc.text("Where to click in the app. Match what you see on screen to the steps on the previous page.", margin, 44);

  // Helper to draw a labelled screenshot block
  const drawShot = (yTop, label, caption, img, maxH = 999) => {
    const innerW = pageW - margin * 2;
    // Render the image scaled to fit width, then clamp height
    let w = innerW;
    let h = (w * img.height) / img.width;
    if (h > maxH) {
      h = maxH;
      w = (h * img.width) / img.height;
    }
    const xImg = margin + (innerW - w) / 2;
    // Label badge
    doc.setFillColor(0, 86, 122);
    doc.rect(margin, yTop, 28, 16, "F");
    doc.setTextColor(255, 255, 255).setFont("helvetica", "bold").setFontSize(10);
    doc.text(label, margin + 14, yTop + 11, { align: "center" });
    // Title + caption
    doc.setTextColor(20, 20, 20).setFont("helvetica", "bold").setFontSize(10);
    doc.text(caption.title, margin + 36, yTop + 8);
    doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(80, 80, 80);
    doc.text(caption.body, margin + 36, yTop + 20, { maxWidth: innerW - 40 });
    // Image with thin border
    const imgY = yTop + 30;
    doc.setDrawColor(200, 200, 200);
    doc.rect(xImg, imgY, w, h);
    doc.addImage(img.data, "PNG", xImg, imgY, w, h, undefined, "FAST");
    return imgY + h; // bottom Y
  };

  let y = 80;
  y = drawShot(y, "A", {
    title: "1 · New Gate-IN form",
    body: "Click + New gate event (or the floating + button on any screen). Type or OCR-scan the container, pick the carrier, set grade & condition, then Save.",
  }, GATE_IN_FORM, 230);

  y += 14;
  y = drawShot(y, "B", {
    title: "2 · Gate OUT from Containers in the yard",
    body: "Find the unit in Containers in the yard (search by number, or filter by carrier). Press the orange Gate OUT button on its row to release it.",
  }, CONTAINER_REGISTER, 180);

  y += 14;
  drawShot(y, "C", {
    title: "3 · Gate Activity log — your audit trail",
    body: "Every IN and OUT lands here in chronological order. Each row links to its EIR and the CODECO EDI message that was emitted.",
  }, GATE_ACTIVITY_LIST, 140);

  // Footer
  doc.setFont("helvetica", "normal").setFontSize(7).setTextColor(140, 140, 140);
  doc.text(
    "Tip: the floating + button at the bottom-right is the fastest way to record a Gate-IN or Gate-OUT from anywhere in the app.",
    margin, pageH - 18
  );

  applyMtgFooterToAllPages(doc);
  doc.save(`portside-gate-guide.pdf`);
}

